// 通用对话 & 流式moe对话
import { useTranslation } from "react-i18next";
import { META_DATA } from "logic/src/constants";
import cls from "classnames";
import { observer } from "mobx-react";
import {
  useEffect,
  useState,
  createContext,
  useRef,
  useCallback,
  Fragment,
  useMemo,
} from "react";
import { useParams } from "react-router";
import { createPortal } from "react-dom";
import { useDebounceFn } from "ahooks";
import _ from "lodash";
import RefList from "@/components/RefList";
import { Tooltip, Popover } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { AgreeType } from "logic/src/objects/session/types";
import { UploadedFile } from "logic/src/objects/file/types";
import { useNavigate } from "react-router-dom";
import { WaterMark } from "antd-mobile";
import i18next from "../../i18n";
import Chat from "logic/src/objects/chat/model";
import SessionList from "./components/SessionList";
import { ESessionFileType } from "logic/src/objects/session/type";
import FileDisplay from "logic/src/objects/file/components/FileDisplay";
import RichmodalVM from "logic/src/businesses/richmodal/vm";
import useMessage from "logic/src/utils/useMessage";
import useScroll from "logic/src/utils/useScroll";
import { ReactComponent as Plus } from "logic/src/components/icons/plus.svg";
import { ReactComponent as LogoWithName } from "logic/src/components/icons/logoWithName.svg";
import { ReactComponent as Logo } from "logic/src/components/icons/logo.svg";
import { ReactComponent as Fold } from "logic/src/components/icons/fold.svg";
import { ReactComponent as UnFold } from "logic/src/components/icons/unFold.svg";
import { ReactComponent as DeepThink } from "logic/src/components/icons/deepthink.svg";
import AppDownLoadGuide from "@/components/AppDownLoadGuide";
import SJButton from "@/components/SJButton";
import FeedBackBar from "@/components/Feedback/FeedBackBar";
import SocialMedia from "@/components/SocialMedia";
import ModelSelector from "@/components/ModelSelector";
import VideoUnderstandingApplyDialog from "@/components/VideoUnderstandingEntry/VideoUnderstandingApplyDialog";
import PopAI from "./components/PopAI";
import PopUser from "./components/PopUser";
import ChatFileFolder from "@/components/ChatFileFolder";
import QuestionInput from "@/components/QuestionInput_v2";
import ChatSample from "./components/ChatSample";
import UserCenterFooter from "./components/UserCenterFooter";
import LoginDialog from "./components/LoginDialog";
import {
  MAX_UPLOAD_FILE_NUMBER,
  MAX_UPLOAD_FILE_SIZE,
  MAX_UPLOAD_IMAGE_NUMBER,
} from "logic/src/objects/file/constants";
import Guess from "logic/src/components/Guess";
import {
  EFileQueryType,
  ISelection,
  EChatModel,
} from "logic/src/objects/chat/types";
import { IChatSample } from "logic/src/objects/chatSample/types";
import authVM from "logic/src/businesses/auth/vm";
import { getQAFromDom, getGuessFromDom } from "./utils/utils";
import { sendCLK } from "logic/src/utils/eventTrack";
import styles from "./index.module.scss";

export const ChatContentContext = createContext<any>({ ref: null });
const accept = ".pdf, .doc, .docx, .ppt, .pptx, .txt, .md, .epub, .mobi";
const acceptImage = ".png, .jpg, .jpeg, .webp";
const uploadOptions = {
  maxFileNumber: MAX_UPLOAD_FILE_NUMBER,
  maxImageNumber: MAX_UPLOAD_IMAGE_NUMBER,
  maxFileSize: MAX_UPLOAD_FILE_SIZE,
  accept: accept,
  acceptImage: acceptImage,
  acceptTypes: [...accept.split(", "), ...acceptImage.split(", ")],
};
const Load_Threshold = 0;
const CHAT_REFRESH_KEY = "isChatRefresh";

const ChatPage = () => {
  const { t } = useTranslation();
  const {
    wrapperRef,
    bsRef,
    init,
    scrollViewToBottomImmediately,
    scrollViewToBottom,
    scrollDirectionRef,
  } = useScroll({
    autoScroll: true,
  });
  const messageApi = useMessage();
  const { id: randomId } = useParams();
  const navigate = useNavigate();
  const inputRef = useRef<any>();
  const transInputRef = useRef<any>();
  const [watermarkContent, setWatermarkContent] = useState("");
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [isFoldBar, setIsFoldBar] = useState<boolean>(false);
  const [folderToolTipOpen, setFolderToolTipOpen] = useState<boolean>(false);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [richmodalVM] = useState(() => new RichmodalVM());
  const [isTransMode, setIsTransMode] = useState(false);
  const [isQASession, setIsQASession] = useState(false);
  const { id } = useParams();

  const selectedGuessItemRef = useRef<string>("");
  const chatSampleRef = useRef<any>(null);

  useEffect(() => {
    const isChatRefresh = localStorage.getItem(CHAT_REFRESH_KEY) === "true";
    richmodalVM.init(isChatRefresh ? undefined : getQAFromDom()).then(() => {
      if (id) {
        const guessList = getGuessFromDom();
        if (guessList) {
          richmodalVM.guess.setGuessList(guessList);
        }
      }
    });

    localStorage.removeItem(CHAT_REFRESH_KEY);
  }, []);

  useEffect(() => {
    if (isTransMode && !richmodalVM.isLogin) {
      setIsTransMode(false);
    }
  }, [richmodalVM.isLogin]);

  useEffect(() => {
    if (isTransMode && richmodalVM.previewingFile) {
      handleToggleInput();
    }
  }, [richmodalVM.previewingFile]);

  useEffect(() => {
    handleInputFocus();
  }, [isTransMode]);

  useEffect(() => {
    !showLoginDialog && setIsQASession(false);
  }, [showLoginDialog]);

  const foldBar = () => {
    setFolderToolTipOpen(false);
    setIsFoldBar(true);
  };

  const unFoldBar = () => {
    setFolderToolTipOpen(false);
    setIsFoldBar(false);
  };

  const handleShowAgreement = (agreeType: AgreeType) => {
    window.open(`/policy?id=${agreeType}&lang=${i18next.language}`, "_blank");
  };

  const gotoLogin = useCallback(() => {
    setShowLoginDialog(true);
  }, []);

  const openLoginByAction = (isNewChat?: boolean) => {
    setIsQASession(!isNewChat);
    gotoLogin();
  };

  const { run: handleContentScroll } = useDebounceFn(
    () => {
      if (wrapperRef.current) {
        const { scrollTop } = wrapperRef.current;

        const canLoadMore =
          richmodalVM.canFetchHistory &&
          scrollDirectionRef.current === "down" &&
          // scrollHeight > clientHeight &&
          scrollTop <= Load_Threshold;

        if (canLoadMore) {
          const firstChild = bsRef.current?.firstElementChild;
          richmodalVM.session?.getChatInstanceList().then(hasMore => {
            if (hasMore) {
              firstChild?.scrollIntoView();
              wrapperRef.current!.scrollTop -= 100;
            }
          });
        }
      }
    },
    {
      wait: 100,
    },
  );

  const handleSessionChange = async (id: string, tempId?: string) => {
    setIsTransMode(false);
    inputRef.current?.clear();

    await richmodalVM.selectSession(id, tempId);
    handleInputFocus();
    setTimeout(() => {
      scrollViewToBottomImmediately();
      init();
    }, 100);
  };

  const handleSendMessage = (
    text: string,
    fileList: UploadedFile[],
    fileQueryInfo?: ISelection,
    type?: EFileQueryType,
    guess?: boolean,
  ) => {
    sendCLK(
      "text_chat_general",
      "文本聊天_触发聊天_" +
        (richmodalVM.chatModel == EChatModel.R1 ? "深度思考" : "普通聊天"),
    );
    richmodalVM.sendMsg(text, fileList, fileQueryInfo, type, guess);
    handleAfterSend();
  };

  const handleSendMessageAndFoldbar = (
    text: string,
    fileList: UploadedFile[],
    fileQueryInfo?: ISelection,
    type?: EFileQueryType,
    guess?: boolean,
  ) => {
    if (!isFoldBar) {
      setIsFoldBar(true);
    }
    handleSendMessage(text, fileList, fileQueryInfo, type, guess);
  };

  const handleReAsk = (chat: Chat) => {
    if (richmodalVM.currentWebSearch.length > 0) {
      richmodalVM.setCurrentWebSearch([]);
    }
    richmodalVM.reAsk(chat);
  };

  const handleSendTransMessage = (text: string) => {
    richmodalVM.sendTransMsg(text);
    handleAfterSend();
  };

  const handleAfterSend = () => {
    handleInputFocus();
    setTimeout(() => {
      scrollViewToBottom();
    }, 300);
  };

  const handleSelectSample = (selectedSample: IChatSample) => {
    richmodalVM.selectSample(selectedSample);
    handleInputFocus();
  };

  const handleNewSession = () => {
    sendCLK("click_new_conversation", "点击发起新对话");
    if (!richmodalVM.isLogin) {
      return openLoginByAction(true);
    }
    if (
      !richmodalVM.session?.session_id &&
      !richmodalVM.session.chatInstanceList.length
    ) {
      handleInputFocus();
      return messageApi.info(t("sensechat-248"));
    }
    richmodalVM.newSession();
    if (isTransMode) {
      handleToggleInput();
    }
    handleInputFocus();
  };

  const handleToggleInput = () => {
    if (!richmodalVM.isLogin) {
      return openLoginByAction(true);
    }
    if (isTransMode) {
      // 关闭翻译输入框
      const inputText = transInputRef.current?.getInputValue();
      inputRef.current?.setInputValue(inputText);
      transInputRef.current?.clear();
    } else {
      // 展示翻译输入框
      const inputText = inputRef.current?.getInputValue();
      transInputRef.current?.setInputValue(inputText);
      inputRef.current?.clear();
      richmodalVM.setFileSelectText(null);
    }
    setIsTransMode(!isTransMode);
  };

  const handleInputFocus = () => {
    if (richmodalVM.isLogin) {
      isTransMode
        ? transInputRef.current?.focusInput()
        : inputRef.current?.focusInput();
    }
  };

  const handleResetHome = () => {
    if (richmodalVM.chat) {
      richmodalVM.resetHome();
    }
  };

  const handleFileCut = useCallback((v: File) => {
    inputRef.current?.uploadFile?.(v);
  }, []);

  let previewFileName = "";
  if (richmodalVM.previewingFile) {
    previewFileName =
      richmodalVM.previewingFile.file_name +
      ([ESessionFileType.image, ESessionFileType.link].includes(
        richmodalVM.previewingFile.type,
      )
        ? `.${richmodalVM.previewingFile.type}`
        : "");
  }

  const handleFinishLogin = async () => {
    const presetMsg = isQASession ? getQAFromDom() : undefined;
    setShowLoginDialog(false);
    await RichmodalVM.cleanCachedSessionInfo();
    await richmodalVM.init(presetMsg);
    if (selectedGuessItemRef.current) {
      handleSendMessage(
        selectedGuessItemRef.current,
        [],
        undefined,
        undefined,
        true,
      );
      selectedGuessItemRef.current = "";
    } else if (presetMsg) {
      if (id) {
        const guessList = getGuessFromDom();
        if (guessList) {
          richmodalVM.guess.setGuessList(guessList);
        }
      }
    }
    handleInputFocus();
  };

  const handleLoginDialogClose = () => {
    selectedGuessItemRef.current = "";
    setShowLoginDialog(false);
  };

  // const startLive = () => {
  //   liveInterval.current = setInterval(() => {
  //     live();
  //   }, 60 * 1000);
  // }

  // const stopLive = useCallback(() => {
  //   if(liveInterval.current) clearInterval(liveInterval.current as any)
  // }, [])

  const handleFinishResetPassword = async () => {
    setShowLoginDialog(false);
  };

  const handleQuery = useCallback(
    (v: EFileQueryType, selection: ISelection) => {
      if (richmodalVM.isPending) {
        messageApi.info("正在回答上一个问题");
      } else {
        richmodalVM.selectFileText(v, selection);
      }
    },
    [],
  );

  const handleGuessClick = (item: string) => {
    sendCLK("text_chat_recommendation", "文本聊天_猜你喜欢内容点击");
    if (id) {
      if (richmodalVM.isLogin) {
        handleSendMessage(item, [], undefined, undefined, true);
      } else {
        selectedGuessItemRef.current = item;
        openLoginByAction(false);
      }
    } else {
      handleSendMessage(item, [], undefined, undefined, true);
    }
  };

  const handleChatErrorRefresh = () => {
    if (getQAFromDom()) {
      localStorage.setItem(CHAT_REFRESH_KEY, "true");
    }
    location.reload();
  };

  const handleSwitchToVideo = async () => {
    if (!richmodalVM.isLogin) {
      return openLoginByAction(true);
    }
    const status = await richmodalVM.user.checkVideoWhiteList();
    if (!status) {
      return;
    }
    // if (status === "applying") {
    //   // 已提交申请，正在审核，使用messageApi显示消息
    //   messageApi.success("您的申请正在审核中，请耐心等待");
    // } else if (status === "unauthorized") {
    //   // 无权限且未申请，显示申请试用弹窗

    //   setShowApplyDialog(true);
    // } else if (status === "authorized") {
    // 有权限，跳转到视频理解页面
    navigate("/videoUnderstanding");
    // }
  };

  const handleLogoutAndLogOff = () => {
    if (id) {
      window.location.reload();
    } else {
      richmodalVM.resetInit();
    }
  };

  const deepthinkClassName = cls(
    styles.chatTypeSelector,
    richmodalVM.chatModel == EChatModel.R1 && styles.selected,
    richmodalVM.web_search && styles.disabled,
  );

  const handleToggleDeepThink = () => {
    if (!richmodalVM.web_search) {
      richmodalVM.setChatModel(
        richmodalVM.chatModel === EChatModel.R1
          ? EChatModel.Normal
          : EChatModel.R1,
      );
    }
  };

  const handleToggleSearch = () => {
    if (richmodalVM.chatModel !== EChatModel.R1) {
      richmodalVM.setWebSearch(!richmodalVM.web_search);
    }
  };

  const searchContent = useMemo(() => {
    return (
      <div>
        <div>根据您的问题智能触发联网搜索</div>
      </div>
    );
  }, []);

  // 资料夹
  const folderTargetDom = document.getElementById("chat-file-folder-dom");
  const chatFolder =
    richmodalVM.isLogin && folderTargetDom
      ? createPortal(
          <ChatFileFolder
            previewFileId={richmodalVM.previewingFile?.file_id}
            onDirFileDelete={richmodalVM.deleteFileFromDir}
            onPreviewFile={richmodalVM.selectPreviewFile}
            fileList={richmodalVM.session.fileManager?.fileList}
          />,
          folderTargetDom,
        )
      : null;
  return (
    <>
      <div className={styles.container}>
        {/* <Header onClickLogo={handleResetHome} /> */}
        {/* <AppQRCode
          className={cls(
            styles.headerQrcode,
            richmodalVM.isLogin && styles.headerQrcodeLogin,
          )}
        /> */}
        <div className={styles.chatContent}>
          {isFoldBar ? (
            <div className={styles.chatContentLeftFold}>
              <Logo className={styles.logo} />
              <div className={styles.foldIcon}>
                <Tooltip
                  open={folderToolTipOpen}
                  placement="top"
                  trigger="hover"
                  color="#242424"
                  title="展开侧栏"
                >
                  <UnFold
                    onClick={unFoldBar}
                    onMouseEnter={() => setFolderToolTipOpen(true)}
                    onMouseLeave={() => setFolderToolTipOpen(false)}
                  />
                </Tooltip>
              </div>
              <Plus className={styles.plus} onClick={handleNewSession} />
              {richmodalVM.isLogin && (
                <div className={styles.user}>
                  <UserCenterFooter
                    resetInit={handleLogoutAndLogOff}
                    fold={isFoldBar}
                  />
                </div>
              )}
            </div>
          ) : (
            <div className={styles.chatContentLeft}>
              <div className={styles.logoContainer}>
                <LogoWithName
                  className={styles.logo}
                  style={{
                    cursor: richmodalVM.isLogin ? "initial" : "pointer",
                  }}
                  onClick={handleResetHome}
                />
                <Tooltip
                  open={folderToolTipOpen}
                  placement="top"
                  trigger="hover"
                  color="#242424"
                  title="收起侧栏"
                >
                  <div className={styles.foldIcon}>
                    <Fold
                      onClick={foldBar}
                      onMouseEnter={() => setFolderToolTipOpen(true)}
                      onMouseLeave={() => setFolderToolTipOpen(false)}
                    />
                  </div>
                </Tooltip>
              </div>
              {richmodalVM.sessionManager && (
                <SessionList
                  isLogin={richmodalVM.isLogin}
                  query={richmodalVM.sessionManager?.query}
                  isFetchData={
                    richmodalVM.sessionManager?.query
                      ? richmodalVM.sessionManager?.isQuerying
                      : richmodalVM.sessionManager?.isFetchList
                  }
                  sessionList={
                    richmodalVM.sessionManager?.query
                      ? richmodalVM.sessionManager?.queryListGroup
                      : richmodalVM.sessionManager?.list || []
                  }
                  session={richmodalVM.session}
                  onUnPinned={richmodalVM.sessionManager.unPinned}
                  onPinned={richmodalVM.sessionManager.pinned}
                  onChange={handleSessionChange}
                  onDelete={richmodalVM.deleteSession}
                  onEditName={richmodalVM.editSessionName}
                  onNewSession={handleNewSession}
                  onQuerySession={richmodalVM.sessionManager.querySession}
                  onLoadSession={richmodalVM.sessionManager?.fetchMoreSession}
                />
              )}
              {authVM.isLogin && <FeedBackBar />}

              {authVM.isLogin && META_DATA.area === "hk" && <SocialMedia />}
              {authVM.isLogin && (
                <UserCenterFooter
                  fold={isFoldBar}
                  resetInit={handleLogoutAndLogOff}
                />
              )}
            </div>
          )}
          <div className={styles.chatContentWrap}>
            <div className={styles.ModelSelectorWrap}>
              <ModelSelector onSwitchToVideo={handleSwitchToVideo} />
              <div className={styles.operations}>
                {!richmodalVM.isLogin && (
                  <SJButton
                    block
                    type="primary"
                    className={styles.loginBtn}
                    onClick={() => openLoginByAction(false)}
                  >
                    {/* 登录/注册 */}
                    {t("sensechat-26")}
                  </SJButton>
                )}
                <AppDownLoadGuide />
              </div>
            </div>

            <div
              className={cls(styles.chatContentRight, {
                [styles.chatContentFolded]:
                  !!richmodalVM.previewingFile ||
                  richmodalVM.currentWebSearch.length > 0,
              })}
            >
              <div
                className={styles.loadingWrap}
                style={{
                  display: richmodalVM.session.isFetchingList
                    ? "block"
                    : "none",
                }}
              >
                <LoadingOutlined style={{ fontSize: 20, color: "#C5C5C5" }} />
              </div>
              <div
                ref={wrapperRef}
                onScroll={handleContentScroll}
                className={cls(styles.chatWrap, {
                  [styles.chatSampleWrapper]:
                    richmodalVM.chatSampleManager &&
                    richmodalVM.session.chatInstanceList.length === 0,
                })}
              >
                <div ref={bsRef} className={styles.chatText}>
                  <div></div>
                  {richmodalVM.chat && (
                    <Fragment key="unlogin-chat">
                      <PopUser
                        files={richmodalVM.chat.files}
                        sessionFileIds={
                          richmodalVM.session.fileManager?.fileIds || []
                        }
                        isLogin={richmodalVM.isLogin}
                        messageList={richmodalVM.chat.send_msg!}
                        textMessageList={richmodalVM.chat.inputMessageList}
                        sessionLinkFileIdMap={
                          richmodalVM.session.fileManager?.linkFileIdMap || {}
                        }
                        onPreviewFile={richmodalVM.selectPreviewFile}
                      />
                      <PopAI
                        onReAsk={handleReAsk}
                        chatInfo={richmodalVM.chat}
                        isLast={true}
                      />
                    </Fragment>
                  )}
                  {richmodalVM.session.chatInstanceList.length === 0 &&
                  !richmodalVM.sessionChanging ? (
                    !richmodalVM.chat ? (
                      <div className={styles.chatSampleWrap}>
                        <div className={styles.contentTitle}>
                          <Logo className={styles.logo} />
                          <div className={styles.text}>
                            <h1 className={styles.title}>
                              你好，我是商量智能助手
                            </h1>
                          </div>
                        </div>
                        <div className={styles.fullInputWrap}>
                          <QuestionInput
                            visible={!isTransMode}
                            ref={inputRef}
                            uploadOptions={uploadOptions}
                            fullDisplay={true}
                            // ref={inputRef}
                            isLogin={richmodalVM.isLogin}
                            gotoLogin={openLoginByAction}
                            isFetching={richmodalVM.isPending}
                            handleSendMessage={handleSendMessageAndFoldbar}
                            maxLength={10000}
                            totalToken={richmodalVM.session.total_token}
                            fileSelection={
                              richmodalVM.fileSelectText || undefined
                            }
                            onDeleteFileSelection={richmodalVM.removeFileText}
                            observeBody
                          />
                          <div className={styles.typeSelectBox}>
                            {!richmodalVM.previewingFile && (
                              <div
                                className={deepthinkClassName}
                                onClick={handleToggleDeepThink}
                              >
                                <DeepThink className={styles.typeIcon} />
                                <span className={styles.text}>深度思考</span>
                              </div>
                            )}
                            {/* 搜索开关 */}
                            <Popover content={searchContent} trigger="click">
                              <div className={styles.chatTypeSelector}>
                                <svg
                                  width="5"
                                  height="6"
                                  viewBox="0 0 5 6"
                                  fill="none"
                                >
                                  <circle
                                    cx="2.5"
                                    cy="3"
                                    r="2.5"
                                    fill="#2CBF64"
                                  />
                                </svg>
                                <span className={styles.text}>联网</span>
                              </div>
                            </Popover>
                          </div>
                        </div>
                        <div className={styles.sampleWrap}>
                          <span
                            className={styles.sampleItem}
                            onClick={handleSwitchToVideo}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="20"
                              height="20"
                              viewBox="0 0 20 20"
                              fill="none"
                            >
                              <path
                                d="M14.8198 12.8905L16.8198 18.0105H15.7598L15.2898 16.7705H13.1898L12.7498 18.0105H11.6898L13.6898 12.8905H14.8198ZM14.2498 13.6805H14.2298C14.2298 13.7105 14.2098 13.7805 14.1798 13.8805C14.1498 14.0105 14.1198 14.1005 14.1098 14.1505C14.0898 14.1805 13.8698 14.8105 13.4398 16.0305H15.0998C14.6498 14.7905 14.4198 14.1605 14.4098 14.1305C14.3398 13.9805 14.2898 13.8405 14.2498 13.6805ZM17.3898 18.0105V12.8905H18.3798V18.0105H17.3898ZM9.9998 17.4405C5.93981 17.4405 2.6398 14.1005 2.6398 10.0005C2.6398 5.90047 5.93981 2.57047 9.9998 2.57047C14.0598 2.57047 17.3598 5.90047 17.3598 10.0005C17.3698 10.3705 17.6798 10.6605 18.0498 10.6405C18.3998 10.6305 18.6798 10.3505 18.6898 10.0005C18.6898 5.17047 14.7898 1.23047 9.98981 1.23047C5.18981 1.23047 1.2998 5.17047 1.2998 10.0005C1.2998 14.8405 5.1998 18.7705 9.9998 18.7705C10.3698 18.7705 10.6698 18.4705 10.6698 18.1005C10.6698 17.7305 10.3698 17.4405 9.9998 17.4405ZM13.7298 8.42047L8.9298 5.70047C8.6198 5.53047 8.3198 5.43047 8.0298 5.43047C7.3798 5.43047 6.7098 5.91047 6.7098 6.96047V12.4105C6.7098 13.4805 7.3698 13.9405 8.0298 13.9405C8.3198 13.9405 8.6198 13.8605 8.9298 13.6705L13.7198 10.9505C14.2598 10.6405 14.5898 10.1905 14.5898 9.69047C14.5898 9.19047 14.2698 8.73047 13.7298 8.42047Z"
                                fill="#5D63C5"
                              />
                            </svg>
                            <span>视频理解</span>
                            <span className={styles.betaText}>（试用）</span>
                          </span>
                          {richmodalVM.chatSampleManager && (
                            <ChatSample
                              lineMode={true}
                              samples={richmodalVM.chatSampleManager?.list}
                              flushSample={
                                richmodalVM.chatSampleManager.flushSample
                              }
                              selectSample={handleSelectSample}
                            />
                          )}
                        </div>
                      </div>
                    ) : null
                  ) : richmodalVM.session.chatInstanceList.length > 0 ? (
                    <>
                      {richmodalVM.session.chatInstanceList.map(
                        (item: Chat, index: number) => (
                          <Fragment key={item.id}>
                            <PopUser
                              isTransMsg={item.isTransMsg}
                              onFileSelectionClick={
                                richmodalVM.highLightSelectText
                              }
                              files={item.files}
                              sessionFileIds={
                                richmodalVM.session.fileManager?.fileIds || []
                              }
                              sessionLinkFileIdMap={
                                richmodalVM.session.fileManager
                                  ?.linkFileIdMap || {}
                              }
                              isLogin={richmodalVM.isLogin}
                              messageList={item.send_msg!}
                              textMessageList={item.inputMessageList}
                              onPreviewFile={richmodalVM.selectPreviewFile}
                            />
                            <PopAI
                              onReAsk={handleReAsk}
                              chatInfo={item}
                              isLast={
                                index ===
                                richmodalVM.session!.chatInstanceList.length - 1
                              }
                              onInputFocus={handleInputFocus}
                              onChatErrorRefresh={handleChatErrorRefresh}
                              onWebSearchClick={richmodalVM.showWebSearch}
                            />
                          </Fragment>
                        ),
                      )}
                      <Guess
                        list={richmodalVM.guess.guessList}
                        loading={richmodalVM.guess.loading}
                        onClick={handleGuessClick}
                      />
                    </>
                  ) : (
                    <div className={styles.changeLoadingWrap}>
                      <LoadingOutlined
                        style={{
                          fontSize: 20,
                          color: "#C5C5C5",
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
              {richmodalVM.session.chatInstanceList.length > 0 && (
                <div className={styles.chatFooter}>
                  <div className={styles.typeSelectBox}>
                    {!richmodalVM.previewingFile && (
                      <div
                        className={deepthinkClassName}
                        onClick={handleToggleDeepThink}
                      >
                        <DeepThink className={styles.typeIcon} />
                        <span className={styles.text}>深度思考</span>
                      </div>
                    )}
                    {/* {!richmodalVM.previewingFile && (
                      <div
                        className={searchClassName}
                        onClick={handleToggleSearch}
                      >
                        <SearchIcon className={styles.typeIcon} />
                        <span className={styles.text}>搜索</span>
                      </div>
                    )} */}

                    <Popover content={searchContent} trigger="click">
                      <div className={styles.chatTypeSelector}>
                        <svg width="5" height="6" viewBox="0 0 5 6" fill="none">
                          <circle cx="2.5" cy="3" r="2.5" fill="#2CBF64" />
                        </svg>
                        <span className={styles.text}>联网</span>
                      </div>
                    </Popover>
                  </div>

                  <QuestionInput
                    visible={!isTransMode}
                    ref={inputRef}
                    uploadOptions={uploadOptions}
                    // ref={inputRef}
                    isLogin={richmodalVM.isLogin}
                    gotoLogin={openLoginByAction}
                    isFetching={richmodalVM.isPending}
                    handleSendMessage={handleSendMessage}
                    maxLength={10000}
                    totalToken={richmodalVM.session.total_token}
                    fileSelection={richmodalVM.fileSelectText || undefined}
                    onDeleteFileSelection={richmodalVM.removeFileText}
                    observeBody
                  />
                  <p className={styles.chatFooterPrivacy}>
                    注意：以上回复全部由AI技术合成，请遵守
                    <span
                      onClick={() => handleShowAgreement(AgreeType.userPrivacy)}
                    >
                      《用户协议》
                    </span>
                    和
                    <span
                      onClick={() => handleShowAgreement(AgreeType.infoCollect)}
                    >
                      《隐私政策》
                    </span>
                    。
                  </p>
                </div>
              )}
            </div>
          </div>
          {richmodalVM.previewingFile && (
            <div className={styles.chatContentRightFile}>
              <FileDisplay
                onDeleteSelection={richmodalVM.deleteKeyPoint}
                onCut={handleFileCut}
                onQuery={handleQuery}
                onClose={richmodalVM.closePreviewFile}
                fileId={richmodalVM.previewingFile.file_id}
                fileUrl={richmodalVM.previewingFile.url}
                fileName={previewFileName}
                currentSelection={richmodalVM.showFileSelect || undefined}
                highlightList={richmodalVM.highLightSelectionList}
              />
            </div>
          )}
          {richmodalVM.currentWebSearch.length > 0 && (
            <div className={styles.chatContentWebSearch}>
              <RefList
                list={richmodalVM.currentWebSearch}
                onClose={() => richmodalVM.setCurrentWebSearch([])}
              />
            </div>
          )}
        </div>
      </div>
      {!!authVM.user.watermark && (
        <WaterMark
          gapX={170}
          gapY={150}
          content={[authVM.user.watermark, t("sensechat-42")]} // 内容由AI生成仅供参考
          fontColor="rgba(108, 112, 117, 0.06)"
          fontSize={12}
        />
      )}
      {messageApi.contextHolder}
      <LoginDialog
        visible={showLoginDialog}
        onFinishLogin={handleFinishLogin}
        onFinishResetPassword={handleFinishResetPassword}
        onClose={handleLoginDialogClose}
      />
      <VideoUnderstandingApplyDialog
        visible={showApplyDialog}
        onCancel={() => setShowApplyDialog(false)}
        onSubmitSuccess={() => {}}
      />
      {chatFolder}
    </>
  );
};
export default observer(ChatPage);
